import { useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { SectionHelpTooltip } from "@/components/shared/SectionHelpTooltip";
import {
  GoCardFieldControl,
  type GoCardFieldCommitResult,
} from "@/components/go/GoCardFieldControl";
import {
  ancestorPathsForField,
  buildClassifierTree,
  collectDescendantFields,
  computeProgress,
  filterTreeByQuery,
  findFirstEmptyField,
  findNodeByPath,
  type ClassifierTreeNode,
} from "@/components/go/goClassifierTree";
import { cn } from "@/lib/utils";
import type { GoEntityCardFieldValue, GoFieldProvenancePatch } from "@/types/go-entity-card";

type Props = {
  fields: GoEntityCardFieldValue[];
  loading?: boolean;
  message?: string | null;
  editing?: boolean;
  highlightedFieldKey?: string | null;
  savingFieldKeys?: ReadonlySet<string>;
  onCommit?: (fieldKey: string, value: unknown) => Promise<GoCardFieldCommitResult | void>;
  onMarkNa?: (fieldKey: string) => Promise<GoCardFieldCommitResult | void>;
  onClearNa?: (fieldKey: string) => Promise<GoCardFieldCommitResult | void>;
  onSaveProvenance?: (
    fieldKey: string,
    patch: GoFieldProvenancePatch,
  ) => Promise<GoCardFieldCommitResult | void>;
  onFocusField?: (fieldKey: string) => void;
  /** Внешний запрос выбрать поле (Continue / deep-link). */
  focusFieldKey?: string | null;
  onContinueResult?: (result: "jumped" | "done") => void;
  onRequestPrepare?: () => void;
  /** Внешний вызов Continue (шорткат). */
  continueNonce?: number;
};

function TreeRow({
  node,
  depth,
  selectedPath,
  expanded,
  onToggle,
  onSelect,
}: {
  node: ClassifierTreeNode;
  depth: number;
  selectedPath: string | null;
  expanded: Set<string>;
  onToggle: (path: string) => void;
  onSelect: (path: string) => void;
}) {
  const hasChildren = node.children.length > 0;
  const isOpen = expanded.has(node.path);
  const descCount = collectDescendantFields(node).length;
  const selected = selectedPath === node.path;

  return (
    <div>
      <button
        type="button"
        className={cn(
          "flex w-full items-center gap-1 rounded-sm px-1.5 py-1 text-left text-xs hover:bg-muted/60",
          selected && "bg-primary/10 text-foreground font-medium",
        )}
        style={{ paddingLeft: 4 + depth * 12 }}
        onClick={() => onSelect(node.path)}
      >
        {hasChildren ? (
          <span
            role="button"
            tabIndex={0}
            className="inline-flex h-4 w-4 shrink-0 items-center justify-center text-muted-foreground"
            onClick={(e) => {
              e.stopPropagation();
              onToggle(node.path);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                e.stopPropagation();
                onToggle(node.path);
              }
            }}
          >
            {isOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
          </span>
        ) : (
          <span className="inline-block h-4 w-4 shrink-0" />
        )}
        <span className="min-w-0 flex-1 truncate" title={node.label}>
          {node.label}
        </span>
        <span className="shrink-0 text-[10px] text-muted-foreground tabular-nums">{descCount}</span>
      </button>
      {hasChildren && isOpen
        ? node.children.map((ch) => (
            <TreeRow
              key={ch.path}
              node={ch}
              depth={depth + 1}
              selectedPath={selectedPath}
              expanded={expanded}
              onToggle={onToggle}
              onSelect={onSelect}
            />
          ))
        : null}
    </div>
  );
}

/**
 * Двухпанельный классификатор ok20: дерево + поля + прогресс + Continue.
 */
export function CountryClassifierPanel({
  fields,
  loading,
  message,
  editing = false,
  highlightedFieldKey,
  savingFieldKeys,
  onCommit,
  onMarkNa,
  onClearNa,
  onSaveProvenance,
  onFocusField,
  focusFieldKey,
  onContinueResult,
  onRequestPrepare,
  continueNonce = 0,
}: Props) {
  const [q, setQ] = useState("");
  const [selectedPath, setSelectedPath] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());

  const tree = useMemo(() => buildClassifierTree(fields), [fields]);
  const visibleTree = useMemo(() => filterTreeByQuery(tree, q), [tree, q]);
  const progress = useMemo(() => computeProgress(fields), [fields]);

  const selectedNode = useMemo(() => {
    if (!selectedPath) return null;
    return findNodeByPath(tree, selectedPath);
  }, [tree, selectedPath]);

  const panelFields = useMemo(() => {
    if (!selectedNode) return [];
    const all = collectDescendantFields(selectedNode);
    // Прямые листья + поля детей только первого уровня без глубокой каши:
    // показываем ownFields узла и ownFields прямых детей; если пусто — все потомки.
    const direct = [
      ...selectedNode.ownFields,
      ...selectedNode.children.flatMap((c) => c.ownFields),
    ];
    const list = direct.length > 0 ? direct : all;
    const needle = q.trim().toLowerCase();
    if (!needle) return list;
    return list.filter(
      (f) =>
        f.field_key.toLowerCase().includes(needle) ||
        (f.label || "").toLowerCase().includes(needle),
    );
  }, [selectedNode, q]);

  const expandPathToField = (fieldKey: string) => {
    const paths = ancestorPathsForField(fieldKey);
    setExpanded((prev) => {
      const next = new Set(prev);
      for (const p of paths) next.add(p);
      // раскрыть предков intermediate
      for (let i = 0; i < paths.length - 1; i++) next.add(paths[i]!);
      return next;
    });
    const parentPath = paths.length >= 2 ? paths[paths.length - 2]! : paths[0] ?? null;
    setSelectedPath(parentPath ?? paths[paths.length - 1] ?? null);
  };

  useEffect(() => {
    if (!focusFieldKey) return;
    expandPathToField(focusFieldKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- только при смене focusFieldKey
  }, [focusFieldKey]);

  useEffect(() => {
    if (selectedPath || tree.length === 0) return;
    const first = tree[0];
    if (first) {
      setSelectedPath(first.path);
      setExpanded(new Set([first.path]));
    }
  }, [tree, selectedPath]);

  const toggle = (path: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  };

  const handleContinue = () => {
    const next = findFirstEmptyField(fields);
    if (!next) {
      onContinueResult?.("done");
      return;
    }
    expandPathToField(next.field_key);
    onFocusField?.(next.field_key);
    onContinueResult?.("jumped");
  };

  useEffect(() => {
    if (!continueNonce) return;
    handleContinue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [continueNonce]);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="inline-flex items-center gap-1.5 text-sm font-medium text-foreground">
          Классификатор ok20
          <SectionHelpTooltip label="Справка: классификатор Country Hub">
            <p className="font-medium">Зачем</p>
            <p>Дерево по кодам полей + форма секции вместо плоского списка на сотни строк.</p>
            <p className="mt-2 font-medium">Как работать</p>
            <ul className="list-disc pl-4">
              <li>Слева — рубрики; справа — поля выбранного узла.</li>
              <li>«Продолжить заполнение» / Ctrl+J — к первой пустой (не N/A).</li>
              <li>«Подготовить» — профили страны → авто-N/A.</li>
              <li>Тип поля задаёт виджет: число, дата, да/нет, KLS-select.</li>
              <li>Ctrl+Shift+N — N/A для сфокусированного поля; Esc — выход из правки.</li>
            </ul>
          </SectionHelpTooltip>
        </div>
        <div className="flex flex-wrap gap-2">
          {onRequestPrepare ? (
            <Button type="button" size="sm" variant="outline" onClick={onRequestPrepare}>
              Подготовить
            </Button>
          ) : null}
          <Button type="button" size="sm" variant="secondary" onClick={handleContinue}>
            Продолжить заполнение
          </Button>
        </div>
      </div>

      <div className="space-y-1.5 rounded-md border border-border bg-muted/15 px-3 py-2">
        <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
          <span className="text-muted-foreground">
            Прогресс:{" "}
            <span className="font-medium text-foreground tabular-nums">
              {progress.filled}/{progress.total} ({progress.percent}%)
            </span>
            {editing ? <span className="ml-2 text-foreground">· режим правки</span> : null}
          </span>
        </div>
        <Progress value={progress.percent} className="h-1.5" />
        <div className="grid gap-1 sm:grid-cols-2 lg:grid-cols-3">
          {progress.roots.slice(0, 12).map((r) => (
            <button
              key={r.path}
              type="button"
              className="flex items-center gap-2 rounded px-1 py-0.5 text-left text-[11px] hover:bg-muted/50"
              title={r.label}
              onClick={() => {
                setSelectedPath(r.path);
                setExpanded((prev) => new Set(prev).add(r.path));
              }}
            >
              <span className="min-w-0 flex-1 truncate text-muted-foreground">{r.label}</span>
              <span className="shrink-0 tabular-nums text-foreground">{r.percent}%</span>
              <span className="h-1 w-12 overflow-hidden rounded bg-muted">
                <span
                  className="block h-full bg-primary/70"
                  style={{ width: `${r.percent}%` }}
                />
              </span>
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-1">
        <Input
          placeholder="Поиск по дереву: столица, 1.1.4…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="h-8 text-sm"
        />
      </div>

      {loading ? (
        <div className="text-xs text-muted-foreground">Загрузка классификатора…</div>
      ) : fields.length === 0 ? (
        <div className="text-xs text-muted-foreground">{message ?? "Нет полей шаблона"}</div>
      ) : (
        <div className="grid min-h-[320px] gap-3 md:grid-cols-[minmax(200px,280px)_1fr]">
          <div className="max-h-[min(70vh,640px)] overflow-y-auto rounded-md border border-border bg-card p-1">
            {visibleTree.length === 0 ? (
              <div className="p-2 text-xs text-muted-foreground">Нет совпадений</div>
            ) : (
              visibleTree.map((n) => (
                <TreeRow
                  key={n.path}
                  node={n}
                  depth={0}
                  selectedPath={selectedPath}
                  expanded={expanded}
                  onToggle={toggle}
                  onSelect={setSelectedPath}
                />
              ))
            )}
          </div>
          <div className="max-h-[min(70vh,640px)] space-y-2 overflow-y-auto rounded-md border border-border bg-muted/10 p-3">
            {!selectedNode ? (
              <div className="text-xs text-muted-foreground">Выберите узел слева</div>
            ) : panelFields.length === 0 ? (
              <div className="text-xs text-muted-foreground">
                В узле «{selectedNode.label}» нет полей — раскройте подраздел.
              </div>
            ) : (
              <>
                <div className="text-xs text-muted-foreground">
                  {selectedNode.label} · полей: {panelFields.length}
                  {panelFields.length > 60 ? " (прокрутите список)" : ""}
                </div>
                {panelFields.map((fv) => (
                  <div
                    key={fv.field_key}
                    className="rounded-md border border-border bg-card px-3 py-2"
                  >
                    <GoCardFieldControl
                      field={fv}
                      editing={editing}
                      highlighted={highlightedFieldKey === fv.field_key}
                      saving={savingFieldKeys?.has(fv.field_key)}
                      showFieldKey
                      onCommit={onCommit}
                      onMarkNa={onMarkNa}
                      onClearNa={onClearNa}
                      onSaveProvenance={onSaveProvenance}
                      onFocusField={onFocusField}
                    />
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
